import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../components/ConfirmDialog';
import {
  applyHealthHouseStationModel,
  createHealthHouseDeviceModel,
  createHealthHouseStation,
  deleteHealthHouseDeviceModel,
  deleteHealthHouseStation,
  listHealthHouseDeviceModels,
  listHealthHouseMeasurementProjects,
  listHealthHouseStations,
  updateHealthHouseDeviceModel,
  updateHealthHouseStationCapabilities,
  updateHealthHouseStation
} from '../api/healthHouseStationsApi';
import { listClinics } from '../api/clinicsApi';
import './HealthHouseStations.css';

const DialogPortal = ({ children }) => createPortal(children, document.body);

const emptyStationForm = {
  clinic_id: '',
  code: '',
  name: '',
  base_url: '',
  provider: 'external',
  connection_mode: 'direct',
  callback_port: '',
  callback_host: '',
  device_identifier: '',
  device_model_id: '',
  capability_statuses: {},
  enabled: true,
  notes: ''
};

const isConnectorMode = (mode) => mode === 'connector';
const isBuiltinMock = (station) => station.provider === 'builtin_mock';
const isNativeAdapterStation = (station) => station && station.connection_mode === 'connector' && !station.base_url;
const isConnectorStation = (station) => !isBuiltinMock(station) && isConnectorMode(station.connection_mode);

const normalizeFormFromStation = (station) => ({
  clinic_id: station?.clinic_id ? String(station.clinic_id) : '',
  code: station?.code || '',
  name: station?.name || '',
  base_url: station?.base_url || '',
  provider: station?.provider || 'external',
  connection_mode: station?.connection_mode || 'direct',
  callback_port: station?.callback_port ? String(station.callback_port) : '',
  callback_host: station?.callback_host || '',
  device_identifier: station?.device_identifier || '',
  device_model_id: station?.device_model_id ? String(station.device_model_id) : '',
  capability_statuses: Object.fromEntries(
    (station?.capabilities || []).map((item) => [item.measurement_type_code, item.availability_status])
  ),
  enabled: station?.enabled !== false,
  notes: station?.notes || ''
});

const stationPayloadFromForm = (form) => {
  const callbackPort = String(form.callback_port || '').trim();
  const clinicId = String(form.clinic_id || '').trim();
  const mock = isBuiltinMock(form);
  const isDirectMode = !mock && !isConnectorMode(form.connection_mode);
  return {
    clinic_id: clinicId ? Number(clinicId) : null,
    code: form.code.trim(),
    name: form.name.trim(),
    base_url: mock ? '' : form.base_url.trim().replace(/\/+$/, ''),
    provider: form.provider || 'external',
    connection_mode: mock ? 'direct' : form.connection_mode || 'direct',
    callback_port: isDirectMode && callbackPort ? Number(callbackPort) : null,
    callback_host: isDirectMode ? form.callback_host.trim().toLowerCase() : '',
    device_identifier: mock ? '' : form.device_identifier.trim(),
    enabled: Boolean(form.enabled),
    notes: form.notes.trim()
  };
};

const emptyModelForm = {
  code: '',
  name: '',
  description: '',
  enabled: true,
  measurement_type_codes: []
};

const formatDateTime = (value) => {
  if (!value) {
    return '-';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '-';
  }

  const pad = (part) => String(part).padStart(2, '0');
  return [
    parsed.getFullYear(),
    pad(parsed.getMonth() + 1),
    pad(parsed.getDate())
  ].join('/') + ` ${pad(parsed.getHours())}:${pad(parsed.getMinutes())}:${pad(parsed.getSeconds())}`;
};

const getRequestErrorMessage = (error, fallback) => {
  return error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback;
};

const HealthHouseStations = () => {
  const { t } = useTranslation();
  const [activeView, setActiveView] = useState('stations');
  const [stations, setStations] = useState([]);
  const [deviceModels, setDeviceModels] = useState([]);
  const [measurementProjects, setMeasurementProjects] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clinicsLoading, setClinicsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingStationId, setEditingStationId] = useState(null);
  const [form, setForm] = useState(emptyStationForm);
  const [showModelForm, setShowModelForm] = useState(false);
  const [editingModelId, setEditingModelId] = useState(null);
  const [modelForm, setModelForm] = useState(emptyModelForm);
  const [capabilitySaving, setCapabilitySaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    stationId: null,
    stationName: ''
  });
  const [clinicDetails, setClinicDetails] = useState(null);

  const selectedStation = useMemo(
    () => stations.find((station) => station.id === editingStationId) || null,
    [stations, editingStationId]
  );

  const selectedDeviceModel = useMemo(
    () => deviceModels.find((deviceModel) => deviceModel.id === editingModelId) || null,
    [deviceModels, editingModelId]
  );

  const loadStations = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await listHealthHouseStations({ include_disabled: true });
      setStations(Array.isArray(data) ? data : []);
    } catch (requestError) {
      console.error('Failed to load health house stations:', requestError);
      setError(getRequestErrorMessage(requestError, t('healthHouseStations.fetchError')));
    } finally {
      setLoading(false);
    }
  };

  const loadClinics = async () => {
    try {
      setClinicsLoading(true);
      const data = await listClinics({ limit: 1000, offset: 0 });
      setClinics(Array.isArray(data) ? data : []);
    } catch (requestError) {
      console.error('Failed to load clinics:', requestError);
      setError(getRequestErrorMessage(requestError, t('healthHouseStations.clinicFetchError')));
    } finally {
      setClinicsLoading(false);
    }
  };

  const loadCapabilityCatalog = async () => {
    try {
      const [models, projects] = await Promise.all([
        listHealthHouseDeviceModels({ include_disabled: true }),
        listHealthHouseMeasurementProjects()
      ]);
      setDeviceModels(Array.isArray(models) ? models : []);
      setMeasurementProjects(Array.isArray(projects) ? projects : []);
    } catch (requestError) {
      console.error('Failed to load Health House capability catalog:', requestError);
      setError(getRequestErrorMessage(requestError, t('healthHouseStations.capabilityFetchError')));
    }
  };

  useEffect(() => {
    loadStations();
    loadClinics();
    loadCapabilityCatalog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showForm || saving) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        resetForm();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showForm, saving]);

  const resetForm = () => {
    setEditingStationId(null);
    setForm(emptyStationForm);
    setShowForm(false);
  };

  const beginCreate = () => {
    setError('');
    setNotice('');
    setEditingStationId(null);
    setForm(emptyStationForm);
    setShowForm(true);
  };

  const beginEdit = (station) => {
    setError('');
    setNotice('');
    setEditingStationId(station.id);
    setForm(normalizeFormFromStation(station));
    setShowForm(true);
  };

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'provider' && value === 'builtin_mock'
        ? { connection_mode: 'direct', base_url: '', callback_host: '', callback_port: '', device_identifier: '' }
        : {}),
      ...(field === 'connection_mode' && value === 'connector'
        ? { callback_host: '', callback_port: '' }
        : {})
    }));
  };

  const validateForm = () => {
    if (!form.code.trim() || !form.name.trim() || (form.provider !== 'builtin_mock' && !form.base_url.trim() && !isNativeAdapterStation(selectedStation))) {
      setError(t('healthHouseStations.requiredFields'));
      return false;
    }

    if (!['direct', 'connector'].includes(form.connection_mode)) {
      setError(t('healthHouseStations.invalidConnectionMode'));
      return false;
    }

    const callbackPort = String(form.callback_port || '').trim();
    if (!isBuiltinMock(form) && callbackPort) {
      const parsedPort = Number(callbackPort);
      if (!Number.isInteger(parsedPort) || parsedPort < 1 || parsedPort > 65535) {
        setError(t('healthHouseStations.invalidPort'));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setNotice('');
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      const payload = editingStationId
        ? stationPayloadFromForm(form)
        : stationPayloadFromForm({ ...form, connection_mode: 'direct' });
      if (editingStationId) {
        await updateHealthHouseStation(editingStationId, payload);
      } else {
        const createdStation = await createHealthHouseStation(payload);
        if (form.device_model_id) {
          await applyHealthHouseStationModel(createdStation.id, {
            device_model_id: Number(form.device_model_id),
            expected_revision: createdStation.capability_revision || 0
          });
        }
      }
      setNotice(t(editingStationId ? 'healthHouseStations.updateSuccess' : 'healthHouseStations.createSuccess'));
      resetForm();
      await loadStations();
    } catch (requestError) {
      console.error('Failed to save health house station:', requestError);
      const fallback = requestError?.response?.status === 409
        ? t('healthHouseStations.conflictError')
        : t('healthHouseStations.saveError');
      setError(getRequestErrorMessage(requestError, fallback));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (station) => {
    try {
      setNotice('');
      setError('');
      await updateHealthHouseStation(station.id, {
        ...stationPayloadFromForm(normalizeFormFromStation(station)),
        enabled: !station.enabled
      });
      await loadStations();
    } catch (requestError) {
      console.error('Failed to toggle health house station:', requestError);
      setError(getRequestErrorMessage(requestError, t('healthHouseStations.saveError')));
    }
  };

  const handleDelete = (station) => {
    setDeleteConfirm({
      show: true,
      stationId: station.id,
      stationName: `#${station.id} · ${station.code} · ${station.name}`
    });
  };

  const confirmDelete = async () => {
    try {
      setNotice('');
      setError('');
      await deleteHealthHouseStation(deleteConfirm.stationId);
      setNotice(t('healthHouseStations.deleteSuccess'));
      setDeleteConfirm({ show: false, stationId: null, stationName: '' });
      if (editingStationId === deleteConfirm.stationId) {
        resetForm();
      }
      await loadStations();
    } catch (requestError) {
      console.error('Failed to delete health house station:', requestError);
      setError(getRequestErrorMessage(requestError, t('healthHouseStations.deleteError')));
    }
  };

  const refreshAll = async () => {
    await Promise.all([loadStations(), loadCapabilityCatalog()]);
  };

  const beginCreateModel = () => {
    setError('');
    setNotice('');
    setEditingModelId(null);
    setModelForm(emptyModelForm);
    setShowModelForm(true);
  };

  const beginEditModel = (deviceModel) => {
    setError('');
    setNotice('');
    setEditingModelId(deviceModel.id);
    setModelForm({
      code: deviceModel.code || '',
      name: deviceModel.name || '',
      description: deviceModel.description || '',
      enabled: deviceModel.enabled !== false,
      measurement_type_codes: (deviceModel.projects || []).map((project) => project.measurement_type_code)
    });
    setShowModelForm(true);
  };

  const resetModelForm = () => {
    setEditingModelId(null);
    setModelForm(emptyModelForm);
    setShowModelForm(false);
  };

  const toggleModelProject = (code) => {
    setModelForm((previous) => ({
      ...previous,
      measurement_type_codes: previous.measurement_type_codes.includes(code)
        ? previous.measurement_type_codes.filter((item) => item !== code)
        : [...previous.measurement_type_codes, code]
    }));
  };

  const saveDeviceModel = async (event) => {
    event.preventDefault();
    if (!modelForm.code.trim() || !modelForm.name.trim() || modelForm.measurement_type_codes.length === 0) {
      setError(t('healthHouseStations.modelRequiredFields'));
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = {
        ...modelForm,
        code: modelForm.code.trim(),
        name: modelForm.name.trim(),
        description: modelForm.description.trim(),
        ...(editingModelId ? { expected_revision: selectedDeviceModel?.revision } : {})
      };
      if (editingModelId) {
        await updateHealthHouseDeviceModel(editingModelId, payload);
      } else {
        await createHealthHouseDeviceModel(payload);
      }
      setNotice(t(editingModelId ? 'healthHouseStations.modelUpdateSuccess' : 'healthHouseStations.modelCreateSuccess'));
      resetModelForm();
      await refreshAll();
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError, t('healthHouseStations.modelSaveError')));
    } finally {
      setSaving(false);
    }
  };

  const removeDeviceModel = async (deviceModel) => {
    try {
      setError('');
      const result = await deleteHealthHouseDeviceModel(deviceModel.id);
      setNotice(t(result?.action === 'disabled'
        ? 'healthHouseStations.modelDisabledBecauseUsed'
        : 'healthHouseStations.modelDeleteSuccess'));
      await refreshAll();
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError, t('healthHouseStations.modelDeleteError')));
    }
  };

  const applySelectedModel = async () => {
    if (!selectedStation || !form.device_model_id) {
      setError(t('healthHouseStations.modelSelectionRequired'));
      return;
    }
    try {
      setCapabilitySaving(true);
      setError('');
      const station = await applyHealthHouseStationModel(selectedStation.id, {
        device_model_id: Number(form.device_model_id),
        expected_revision: selectedStation.capability_revision || 0
      });
      setForm(normalizeFormFromStation(station));
      setNotice(t('healthHouseStations.stationModelApplied'));
      await refreshAll();
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError, requestError?.response?.status === 409
        ? t('healthHouseStations.revisionConflict')
        : t('healthHouseStations.capabilitySaveError')));
      if (requestError?.response?.status === 409) {
        await loadStations();
      }
    } finally {
      setCapabilitySaving(false);
    }
  };

  const saveCapabilityStatuses = async () => {
    if (!selectedStation) {
      return;
    }
    try {
      setCapabilitySaving(true);
      setError('');
      const station = await updateHealthHouseStationCapabilities(selectedStation.id, {
        expected_revision: selectedStation.capability_revision || 0,
        statuses: form.capability_statuses
      });
      setForm(normalizeFormFromStation(station));
      setNotice(t('healthHouseStations.capabilityUpdateSuccess'));
      await loadStations();
    } catch (requestError) {
      setError(getRequestErrorMessage(requestError, requestError?.response?.status === 409
        ? t('healthHouseStations.revisionConflict')
        : t('healthHouseStations.capabilitySaveError')));
      if (requestError?.response?.status === 409) {
        await loadStations();
      }
    } finally {
      setCapabilitySaving(false);
    }
  };

  const enabledCount = stations.filter((station) => station.enabled).length;
  const busyCount = stations.filter((station) => station.status === 'busy').length;
  const mockCount = stations.filter(isBuiltinMock).length;
  const hasDirectStations = stations.some((station) => !isBuiltinMock(station) && !isConnectorStation(station));
  const connectorStations = stations.filter(isConnectorStation);
  const onlineConnectorCount = connectorStations.filter((station) => station.connector_status === 'online').length;
  const offlineConnectorCount = connectorStations.filter((station) => station.connector_status === 'offline').length;
  const selectedFormModel = deviceModels.find((deviceModel) => String(deviceModel.id) === String(form.device_model_id)) || null;
  const selectedStationCodes = new Set((selectedStation?.capabilities || []).map((item) => item.measurement_type_code));
  const selectedModelCodes = new Set((selectedFormModel?.projects || []).map((item) => item.measurement_type_code));
  const syncAdds = [...selectedModelCodes].filter((code) => !selectedStationCodes.has(code));
  const syncRemoves = [...selectedStationCodes].filter((code) => !selectedModelCodes.has(code));
  const stationNeedsSync = !!selectedStation && !!selectedFormModel && (
    Number(selectedStation.device_model_id) !== Number(selectedFormModel.id)
    || Number(selectedStation.device_model_revision) !== Number(selectedFormModel.revision)
    || syncAdds.length > 0
    || syncRemoves.length > 0
  );

  const formatClinic = (station) => {
    if (station.clinic_name) {
      return station.clinic_id ? `${station.clinic_name} (#${station.clinic_id})` : station.clinic_name;
    }
    if (station.clinic_id) {
      return `#${station.clinic_id}`;
    }
    return t('healthHouseStations.unassignedClinic');
  };

  const getClinicForStation = (station) => {
    if (!station?.clinic_id) {
      return null;
    }
    return clinics.find((clinic) => Number(clinic.id) === Number(station.clinic_id)) || null;
  };

  const beginClinicDetails = (station) => {
    if (!station?.clinic_id) {
      return;
    }
    setClinicDetails({
      station,
      clinic: getClinicForStation(station)
    });
  };

  const closeClinicDetails = () => {
    setClinicDetails(null);
  };

  const renderStatusPill = (station) => {
    if (!station.enabled) {
      return <span className="status-pill status-pill--neutral">{t('healthHouseStations.statusDisabled')}</span>;
    }
    if (station.status === 'busy') {
      return <span className="status-pill status-pill--warning">{t('healthHouseStations.statusBusy')}</span>;
    }
    return <span className="status-pill status-pill--success">{t('healthHouseStations.statusIdle')}</span>;
  };

  const stationModeLabel = (station) => t(isBuiltinMock(station)
    ? 'healthHouseStations.providerMock'
    : isConnectorStation(station)
      ? 'healthHouseStations.connectionModeConnector'
      : 'healthHouseStations.connectionModeDirect');

  const renderConnectorStatus = (station) => {
    if (station.connector_status === 'online') {
      return (
        <>
          <span className="status-pill status-pill--success">
            {t('healthHouseStations.connectorStatusOnline')}
          </span>
          {station.connector_code && (
            <div className="table-meta">{station.connector_code}</div>
          )}
        </>
      );
    }

    return (
      <span className="status-pill status-pill--danger">
        {t('healthHouseStations.connectorStatusOffline')}
      </span>
    );
  };

  const renderStackedLabel = (primary, secondary) => (
    <span className="table-heading-stack">
      <span>{primary}</span>
      <span>{secondary}</span>
    </span>
  );

  const renderCompositeRow = (label, value) => (
    <div className="table-composite__row">
      <span className="table-composite__label">{label}</span>
      <span className="table-composite__value">{value}</span>
    </div>
  );

  const renderEndpointCell = (station) => (
    <div className="table-endpoint">
      {isBuiltinMock(station) ? (
        <div className="table-meta">{t('healthHouseStations.baseUrlHintMock')}</div>
      ) : (
        <>
          <div className="table-title table-title--wrap">{isNativeAdapterStation(station) ? 'Adapter · WebSocket' : station.base_url}</div>
          {isConnectorStation(station) ? (
            <div className="table-meta">{t('healthHouseStations.connectorCallbackNotUsed')}</div>
          ) : (
            <div className="table-composite table-composite--endpoint">
              {renderCompositeRow(t('healthHouseStations.callbackHost'), station.callback_host || '-')}
              {renderCompositeRow(t('healthHouseStations.callbackPort'), station.callback_port || '-')}
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderClinicCell = (station) => {
    if (!station.clinic_id) {
      return <span>{formatClinic(station)}</span>;
    }

    return (
      <button
        type="button"
        className="table-link-button"
        onClick={() => beginClinicDetails(station)}
        title={t('healthHouseStations.viewClinicDetails')}
      >
        {formatClinic(station)}
      </button>
    );
  };

  const formIsMock = isBuiltinMock(form);
  const formIsConnector = isConnectorStation(form);

  return (
    <div className="admin-page">
      <div className="page-header admin-header-panel">
        <div className="page-title-block">
          <h1 className="page-title">{t('healthHouseStations.title')}</h1>
        </div>

        <div className="page-actions">
          <button
            type="button"
            className="theme-button theme-button--primary"
            onClick={activeView === 'stations' ? beginCreate : beginCreateModel}
          >
            {t(activeView === 'stations' ? 'healthHouseStations.newStation' : 'healthHouseStations.newModel')}
          </button>
          <button type="button" className="theme-button theme-button--ghost" onClick={refreshAll} disabled={loading}>
            {t('common.refresh')}
          </button>
        </div>
      </div>

      <div className="capability-view-switch" role="tablist" aria-label={t('healthHouseStations.viewSwitcher')}>
        <button
          type="button"
          role="tab"
          aria-selected={activeView === 'stations'}
          className={activeView === 'stations' ? 'is-active' : ''}
          onClick={() => setActiveView('stations')}
        >
          {t('healthHouseStations.stationView')}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeView === 'models'}
          className={activeView === 'models' ? 'is-active' : ''}
          onClick={() => setActiveView('models')}
        >
          {t('healthHouseStations.modelView')}
        </button>
      </div>

      {error && !showForm && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      {notice && (
        <div className="status-banner status-banner--success">
          <span>{notice}</span>
        </div>
      )}

      <section className="surface-panel admin-panel" hidden={activeView !== 'stations'}>
        <div className="surface-panel__body stack">
          <div className="admin-section-header">
            <div className="admin-stat-strip">
              <span className="status-pill status-pill--success">
                {t('healthHouseStations.enabledSummary', { count: enabledCount })}
              </span>
              <span className="status-pill status-pill--warning">
                {t('healthHouseStations.busySummary', { count: busyCount })}
              </span>
              {mockCount > 0 && (
                <span className="status-pill status-pill--cool">
                  {t('healthHouseStations.mockSummary', { count: mockCount })}
                </span>
              )}
              {connectorStations.length > 0 && (
                <>
                  <span className="status-pill status-pill--success">
                    {t('healthHouseStations.connectorOnlineSummary', { count: onlineConnectorCount })}
                  </span>
                  <span className="status-pill status-pill--danger">
                    {t('healthHouseStations.connectorOfflineSummary', { count: offlineConnectorCount })}
                  </span>
                </>
              )}
              {hasDirectStations && (
                <span className="status-pill status-pill--cool">
                  {t('healthHouseStations.callbackRoutingHint')}
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="loading-orb" />
              <span>{t('common.loading')}</span>
            </div>
          ) : stations.length === 0 ? (
            <div className="empty-state">
              <h3>{t('healthHouseStations.emptyTitle')}</h3>
              <p>{t('healthHouseStations.emptyHint')}</p>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('healthHouseStations.station')}</th>
                    <th>{t('healthHouseStations.boundClinic')}</th>
                    <th>{t('healthHouseStations.baseUrl')}</th>
                    <th>{t('healthHouseStations.connectionMode')}</th>
                    <th className="table-cell--measurement">{t('healthHouseStations.measurementStatus')}</th>
                    <th>{mockCount === stations.length
                      ? t('healthHouseStations.updatedAt')
                      : renderStackedLabel(t('healthHouseStations.lastSeen'), t('healthHouseStations.updatedAt'))}</th>
                    <th>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stations.map((station) => (
                    <tr key={station.id}>
                      <td>
                        <div className="table-title">{station.name}</div>
                        <div className="table-meta">#{station.id} · {station.code}</div>
                        <div className="table-meta table-meta--identifier">
                          {t('healthHouseStations.deviceModel')}: {station.device_model_name || t('healthHouseStations.noModel')}
                          {station.device_model_id && ` · r${station.device_model_revision}/${station.device_model_current_revision}`}
                        </div>
                        <div className="table-meta">
                          {t('healthHouseStations.capabilityRevision')}: {station.capability_revision || 0}
                        </div>
                        {!isBuiltinMock(station) && station.device_identifier && (
                          <div className="table-meta table-meta--identifier">
                            {t('healthHouseStations.deviceIdentifier')}: {station.device_identifier}
                          </div>
                        )}
                        {station.notes && <div className="table-meta">{station.notes}</div>}
                      </td>
                      <td>{renderClinicCell(station)}</td>
                      <td>{renderEndpointCell(station)}</td>
                      <td>
                        <div className="table-composite">
                          {renderCompositeRow(
                            t('healthHouseStations.connectionMode'),
                            <span className="status-pill status-pill--cool">
                              {stationModeLabel(station)}
                            </span>
                          )}
                          {isConnectorStation(station) && renderCompositeRow(t('healthHouseStations.connectorStatus'), renderConnectorStatus(station))}
                        </div>
                      </td>
                      <td className="table-cell--measurement">
                        {renderStatusPill(station)}
                        {station.active_measurement_id && (
                          <div className="table-meta">
                            {t('healthHouseStations.activeMeasurement', { id: station.active_measurement_id })}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className="table-composite table-composite--time">
                          {!isBuiltinMock(station) && renderCompositeRow(t('healthHouseStations.lastSeen'), formatDateTime(station.last_seen_at))}
                          {renderCompositeRow(t('healthHouseStations.updatedAt'), formatDateTime(station.updated_at))}
                        </div>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            type="button"
                            className="theme-button theme-button--ghost theme-button--compact"
                            onClick={() => beginEdit(station)}
                          >
                            {t('common.edit')}
                          </button>
                          {station.provider === 'builtin_mock' && (
                            <button
                              type="button"
                              className="theme-button theme-button--secondary theme-button--compact"
                              onClick={() => { window.location.href = `/web/health-house-stations/${station.id}/mock-config`; }}
                            >
                              {t('healthHouseStations.mockData')}
                            </button>
                          )}
                          <button
                            type="button"
                            className="theme-button theme-button--secondary theme-button--compact"
                            onClick={() => handleToggleEnabled(station)}
                          >
                            {station.enabled ? t('healthHouseStations.disable') : t('healthHouseStations.enable')}
                          </button>
                          <button
                            type="button"
                            className="theme-button theme-button--danger theme-button--compact"
                            onClick={() => handleDelete(station)}
                          >
                            {t('common.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="surface-panel admin-panel" hidden={activeView !== 'models'}>
        <div className="surface-panel__body stack">
          <div className="admin-section-header">
            <span className="status-pill status-pill--cool">
              {t('healthHouseStations.modelCount', { count: deviceModels.length })}
            </span>
          </div>

          <div className="status-banner status-banner--info">
            <span>{t('healthHouseStations.modelInheritanceHint')}</span>
          </div>

          {deviceModels.length === 0 ? (
            <div className="empty-state">
              <h3>{t('healthHouseStations.modelEmptyTitle')}</h3>
              <p>{t('healthHouseStations.modelEmptyHint')}</p>
            </div>
          ) : (
            <div className="data-table-shell">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>{t('healthHouseStations.deviceModel')}</th>
                    <th>{t('healthHouseStations.modelProjects')}</th>
                    <th>{t('healthHouseStations.modelRevision')}</th>
                    <th>{t('healthHouseStations.enabled')}</th>
                    <th>{t('reports.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {deviceModels.map((deviceModel) => (
                    <tr key={deviceModel.id}>
                      <td>
                        <div className="table-title">{deviceModel.name}</div>
                        <div className="table-meta">{deviceModel.code}</div>
                        {deviceModel.description && <div className="table-meta">{deviceModel.description}</div>}
                      </td>
                      <td>
                        <div className="capability-tag-list">
                          {(deviceModel.projects || []).map((project) => (
                            <span className="status-pill status-pill--cool" key={project.measurement_type_code}>
                              {project.measurement_type_code} · {project.name_zh}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td>r{deviceModel.revision}</td>
                      <td>
                        <span className={`status-pill ${deviceModel.enabled ? 'status-pill--success' : 'status-pill--neutral'}`}>
                          {t(deviceModel.enabled ? 'healthHouseStations.modelEnabled' : 'healthHouseStations.modelDisabled')}
                        </span>
                      </td>
                      <td>
                        <div className="table-actions">
                          <button type="button" className="theme-button theme-button--ghost theme-button--compact" onClick={() => beginEditModel(deviceModel)}>
                            {t('common.edit')}
                          </button>
                          <button type="button" className="theme-button theme-button--danger theme-button--compact" onClick={() => removeDeviceModel(deviceModel)}>
                            {t(deviceModel.enabled ? 'healthHouseStations.disableOrDeleteModel' : 'common.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {showForm && (
        <DialogPortal>
          <div className="dialog-backdrop">
            <div
              className="dialog-shell dialog-shell--wide admin-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="health-house-station-form-title"
              onClick={(event) => event.stopPropagation()}
            >
            <div className="page-title-block">
              <h2 id="health-house-station-form-title" className="dialog-shell__title">
                {editingStationId ? selectedStation?.name || t('healthHouseStations.editStation') : t('healthHouseStations.createStation')}
              </h2>
            </div>

            {error && (
              <div className="status-banner status-banner--error">
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="stack">
              <div className="form-grid">
                {!editingStationId && (
                  <div className="field form-grid__full">
                    <span className="field__label">{t('healthHouseStations.creationScope')}</span>
                    <div className="status-banner status-banner--info">
                      <span>{t('healthHouseStations.directCreateOnlyHint')}</span>
                    </div>
                  </div>
                )}

                <div className="field">
                  <label className="field__label" htmlFor="station-provider">{t('healthHouseStations.provider')}</label>
                  <select id="station-provider" className="theme-select" value={form.provider} onChange={(event) => handleFieldChange('provider', event.target.value)} disabled={formIsConnector}>
                    <option value="external">{t('healthHouseStations.providerExternal')}</option>
                    <option value="builtin_mock">{t('healthHouseStations.providerMock')}</option>
                  </select>
                  <span className="field__hint">{t('healthHouseStations.providerHint')}</span>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="station-clinic">{t('healthHouseStations.boundClinic')}</label>
                  <select
                    id="station-clinic"
                    className="theme-select"
                    value={form.clinic_id}
                    onChange={(event) => handleFieldChange('clinic_id', event.target.value)}
                    disabled={clinicsLoading}
                  >
                    <option value="">{t('healthHouseStations.unassignedClinic')}</option>
                    {clinics.map((clinic) => (
                      <option key={clinic.id} value={String(clinic.id)}>
                        #{clinic.id} · {clinic.name}
                      </option>
                    ))}
                  </select>
                  <span className="field__hint">{t('healthHouseStations.boundClinicHint')}</span>
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="station-name">{t('healthHouseStations.name')}</label>
                  <input
                    id="station-name"
                    className="theme-input"
                    type="text"
                    value={form.name}
                    onChange={(event) => handleFieldChange('name', event.target.value)}
                    placeholder={t('healthHouseStations.namePlaceholder')}
                    disabled={formIsConnector}
                  />
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="station-code">{t('healthHouseStations.code')}</label>
                  <input
                    id="station-code"
                    className="theme-input"
                    type="text"
                    value={form.code}
                    onChange={(event) => handleFieldChange('code', event.target.value)}
                    placeholder={t('healthHouseStations.codePlaceholder')}
                    disabled={formIsConnector}
                  />
                </div>

                {!formIsMock && <div className="field">
                  <label className="field__label" htmlFor="station-base-url">{t('healthHouseStations.baseUrl')}</label>
                  <input
                    id="station-base-url"
                    className="theme-input"
                    type="url"
                    value={form.base_url}
                    onChange={(event) => handleFieldChange('base_url', event.target.value)}
                    placeholder="http://192.168.1.20:7810"
                    disabled={formIsConnector}
                  />
                  <span className="field__hint">
                    {formIsConnector
                      ? t('healthHouseStations.baseUrlHintConnector')
                      : t('healthHouseStations.baseUrlHintDirect')}
                  </span>
                </div>}

                <div className="field">
                  <span className="field__label">{t('healthHouseStations.connectionMode')}</span>
                  <div>
                    <span className="status-pill status-pill--cool">
                      {stationModeLabel(form)}
                    </span>
                  </div>
                  {!formIsMock && (
                    <span className="field__hint">
                      {formIsConnector
                        ? t('healthHouseStations.connectorManagedHint')
                        : t('healthHouseStations.directModeHint')}
                    </span>
                  )}
                </div>

                <div className="field">
                  <label className="field__label" htmlFor="station-device-model">{t('healthHouseStations.deviceModel')}</label>
                  <select
                    id="station-device-model"
                    className="theme-select"
                    value={form.device_model_id}
                    onChange={(event) => handleFieldChange('device_model_id', event.target.value)}
                  >
                    <option value="">{t('healthHouseStations.noModel')}</option>
                    {deviceModels
                      .filter((deviceModel) => deviceModel.enabled || Number(deviceModel.id) === Number(selectedStation?.device_model_id))
                      .map((deviceModel) => (
                        <option key={deviceModel.id} value={String(deviceModel.id)}>
                          {deviceModel.name} · r{deviceModel.revision}{deviceModel.enabled ? '' : ` (${t('healthHouseStations.modelDisabled')})`}
                        </option>
                      ))}
                  </select>
                  <span className="field__hint">{t('healthHouseStations.modelSelectionHint')}</span>
                </div>

                {editingStationId && (
                  <div className="field form-grid__full capability-editor">
                    <div className="capability-editor__heading">
                      <div>
                        <span className="field__label">{t('healthHouseStations.stationCapabilities')}</span>
                        <div className="table-meta">
                          {t('healthHouseStations.capabilityRevision')}: {selectedStation?.capability_revision || 0}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="theme-button theme-button--secondary theme-button--compact"
                        onClick={applySelectedModel}
                        disabled={capabilitySaving || !form.device_model_id || !stationNeedsSync}
                      >
                        {stationNeedsSync ? t('healthHouseStations.applyOrSyncModel') : t('healthHouseStations.modelAlreadySynced')}
                      </button>
                    </div>

                    <div className="status-banner status-banner--warning">
                      <span>{t('healthHouseStations.planningOnlyWarning')}</span>
                    </div>

                    {stationNeedsSync && (
                      <div className="capability-diff">
                        <strong>{t('healthHouseStations.syncDifference')}</strong>
                        <span>{t('healthHouseStations.syncAdds', { codes: syncAdds.join(', ') || '-' })}</span>
                        <span>{t('healthHouseStations.syncRemoves', { codes: syncRemoves.join(', ') || '-' })}</span>
                        <span>{t('healthHouseStations.syncPreservesStatuses')}</span>
                      </div>
                    )}

                    {(selectedStation?.capabilities || []).length > 0 ? (
                      <div className="capability-matrix">
                        {(selectedStation.capabilities || []).map((capability) => (
                          <label className="capability-matrix__row" key={capability.measurement_type_code}>
                            <span>
                              <strong>{capability.measurement_type_code} · {capability.name_zh}</strong>
                              <small>{capability.exam_types.join(', ')}</small>
                            </span>
                            <select
                              className="theme-select"
                              value={form.capability_statuses[capability.measurement_type_code] || capability.availability_status}
                              onChange={(event) => setForm((previous) => ({
                                ...previous,
                                capability_statuses: {
                                  ...previous.capability_statuses,
                                  [capability.measurement_type_code]: event.target.value
                                }
                              }))}
                            >
                              <option value="available">{t('healthHouseStations.capabilityAvailable')}</option>
                              <option value="maintenance">{t('healthHouseStations.capabilityMaintenance')}</option>
                              <option value="fault">{t('healthHouseStations.capabilityFault')}</option>
                            </select>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div className="empty-state capability-empty">
                        <p>{t('healthHouseStations.noStationCapabilities')}</p>
                      </div>
                    )}

                    {(selectedStation?.capabilities || []).length > 0 && (
                      <div>
                        <button
                          type="button"
                          className="theme-button theme-button--primary theme-button--compact"
                          onClick={saveCapabilityStatuses}
                          disabled={capabilitySaving}
                        >
                          {t('healthHouseStations.saveCapabilityStatuses')}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {!formIsMock && (formIsConnector ? (
                  <div className="field form-grid__full">
                    <span className="field__label">{t('healthHouseStations.callbackRouting')}</span>
                    <div className="status-banner status-banner--info">
                      <span>{t('healthHouseStations.connectorCallbackHint')}</span>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="field">
                      <label className="field__label" htmlFor="station-callback-host">{t('healthHouseStations.callbackHost')}</label>
                      <input
                        id="station-callback-host"
                        className="theme-input"
                        type="text"
                        value={form.callback_host}
                        onChange={(event) => handleFieldChange('callback_host', event.target.value)}
                        placeholder="hh-station-01.clinic.synyi.com"
                      />
                      <span className="field__hint">{t('healthHouseStations.callbackHostHint')}</span>
                    </div>

                    <div className="field">
                      <label className="field__label" htmlFor="station-callback-port">{t('healthHouseStations.callbackPort')}</label>
                      <input
                        id="station-callback-port"
                        className="theme-input"
                        type="number"
                        min="1"
                        max="65535"
                        value={form.callback_port}
                        onChange={(event) => handleFieldChange('callback_port', event.target.value)}
                        placeholder="18081"
                      />
                      <span className="field__hint">{t('healthHouseStations.callbackPortHint')}</span>
                    </div>
                  </>
                ))}

                {!formIsMock && <div className="field">
                  <label className="field__label" htmlFor="station-device-identifier">{t('healthHouseStations.deviceIdentifier')}</label>
                  <input
                    id="station-device-identifier"
                    className="theme-input"
                    type="text"
                    value={form.device_identifier}
                    onChange={(event) => handleFieldChange('device_identifier', event.target.value)}
                    placeholder={t('healthHouseStations.deviceIdentifierPlaceholder')}
                  />
                </div>}

                <label className="field" htmlFor="station-enabled">
                  <span className="field__label">{t('healthHouseStations.enabled')}</span>
                  <span className="table-meta--inline">
                    <input
                      id="station-enabled"
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(event) => handleFieldChange('enabled', event.target.checked)}
                    />
                    <span>{form.enabled ? t('healthHouseStations.enabledOn') : t('healthHouseStations.enabledOff')}</span>
                  </span>
                </label>
              </div>

              <div className="field">
                <label className="field__label" htmlFor="station-notes">{t('healthHouseStations.notes')}</label>
                <textarea
                  id="station-notes"
                  className="theme-textarea"
                  value={form.notes}
                  onChange={(event) => handleFieldChange('notes', event.target.value)}
                  placeholder={t('healthHouseStations.notesPlaceholder')}
                />
              </div>

              <div className="dialog-shell__actions">
                <button type="submit" className="theme-button theme-button--primary" disabled={saving}>
                  {saving ? t('common.submitting') : t('common.save')}
                </button>
                <button type="button" className="theme-button theme-button--ghost" onClick={resetForm} disabled={saving}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
            </div>
          </div>
        </DialogPortal>
      )}

      {showModelForm && (
        <DialogPortal>
          <div className="dialog-backdrop">
            <div
              className="dialog-shell dialog-shell--wide admin-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="health-house-model-form-title"
              onClick={(event) => event.stopPropagation()}
            >
            <div className="page-title-block">
              <h2 id="health-house-model-form-title" className="dialog-shell__title">
                {t(editingModelId ? 'healthHouseStations.editModel' : 'healthHouseStations.newModel')}
              </h2>
            </div>

            {error && (
              <div className="status-banner status-banner--error"><span>{error}</span></div>
            )}

            <form className="stack" onSubmit={saveDeviceModel}>
              <div className="form-grid">
                <div className="field">
                  <label className="field__label" htmlFor="device-model-name">{t('healthHouseStations.name')}</label>
                  <input
                    id="device-model-name"
                    className="theme-input"
                    value={modelForm.name}
                    onChange={(event) => setModelForm((previous) => ({ ...previous, name: event.target.value }))}
                  />
                </div>
                <div className="field">
                  <label className="field__label" htmlFor="device-model-code">{t('healthHouseStations.code')}</label>
                  <input
                    id="device-model-code"
                    className="theme-input"
                    value={modelForm.code}
                    onChange={(event) => setModelForm((previous) => ({ ...previous, code: event.target.value }))}
                  />
                </div>
                <label className="field" htmlFor="device-model-enabled">
                  <span className="field__label">{t('healthHouseStations.enabled')}</span>
                  <span className="table-meta--inline">
                    <input
                      id="device-model-enabled"
                      type="checkbox"
                      checked={modelForm.enabled}
                      onChange={(event) => setModelForm((previous) => ({ ...previous, enabled: event.target.checked }))}
                    />
                    <span>{t(modelForm.enabled ? 'healthHouseStations.modelEnabled' : 'healthHouseStations.modelDisabled')}</span>
                  </span>
                </label>
                <div className="field form-grid__full">
                  <label className="field__label" htmlFor="device-model-description">{t('healthHouseStations.modelDescription')}</label>
                  <textarea
                    id="device-model-description"
                    className="theme-textarea"
                    value={modelForm.description}
                    onChange={(event) => setModelForm((previous) => ({ ...previous, description: event.target.value }))}
                  />
                </div>
                <div className="field form-grid__full">
                  <span className="field__label">{t('healthHouseStations.modelProjects')}</span>
                  <div className="model-project-grid">
                    {measurementProjects.map((project) => (
                      <label key={project.measurement_type_code} className="model-project-option">
                        <input
                          type="checkbox"
                          checked={modelForm.measurement_type_codes.includes(project.measurement_type_code)}
                          onChange={() => toggleModelProject(project.measurement_type_code)}
                        />
                        <span>
                          <strong>{project.measurement_type_code} · {project.name_zh}</strong>
                          <small>{project.name_en} · {project.exam_types.join(', ')}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="status-banner status-banner--info">
                <span>{t('healthHouseStations.explicitSyncHint')}</span>
              </div>

              <div className="dialog-shell__actions">
                <button type="submit" className="theme-button theme-button--primary" disabled={saving}>
                  {saving ? t('common.submitting') : t('common.save')}
                </button>
                <button type="button" className="theme-button theme-button--ghost" onClick={resetModelForm} disabled={saving}>
                  {t('common.cancel')}
                </button>
              </div>
            </form>
            </div>
          </div>
        </DialogPortal>
      )}

      {clinicDetails && (
        <DialogPortal>
          <div className="dialog-backdrop">
            <div
              className="dialog-shell admin-dialog"
              role="dialog"
              aria-modal="true"
              aria-labelledby="clinic-details-title"
              onClick={(event) => event.stopPropagation()}
            >
            <div className="page-title-block">
              <h2 id="clinic-details-title" className="dialog-shell__title">
                {clinicDetails.clinic?.name || clinicDetails.station.clinic_name || t('clinics.unnamedClinic')}
              </h2>
            </div>

            {!clinicDetails.clinic && (
              <div className="status-banner status-banner--warning">
                <span>{t('healthHouseStations.clinicDetailsUnavailable')}</span>
              </div>
            )}

            <div className="detail-list">
              <div className="detail-row">
                <span>{t('clinics.clinic')}</span>
                <strong>#{clinicDetails.station.clinic_id}</strong>
              </div>
              <div className="detail-row">
                <span>{t('clinics.token')}</span>
                <strong className="table-token">{clinicDetails.clinic?.secret || '-'}</strong>
              </div>
              <div className="detail-row">
                <span>{t('clinics.status')}</span>
                <strong>{clinicDetails.clinic?.status || '-'}</strong>
              </div>
              <div className="detail-row">
                <span>{t('clinics.currentPatientId')}</span>
                <strong>{clinicDetails.clinic?.current_patient_id || '-'}</strong>
              </div>
              <div className="detail-row">
                <span>{t('clinics.currentVisitId')}</span>
                <strong>{clinicDetails.clinic?.current_visit_id || '-'}</strong>
              </div>
              <div className="detail-row">
                <span>{t('clinics.createdAt')}</span>
                <strong>{formatDateTime(clinicDetails.clinic?.created_at)}</strong>
              </div>
              <div className="detail-row">
                <span>{t('clinics.updatedAt')}</span>
                <strong>{formatDateTime(clinicDetails.clinic?.updated_at)}</strong>
              </div>
              <div className="detail-row">
                <span>{t('healthHouseStations.boundStation')}</span>
                <strong>{clinicDetails.station.name} · #{clinicDetails.station.id}</strong>
              </div>
              <div className="detail-row">
                <span>{t('healthHouseStations.connectionMode')}</span>
                <strong>{stationModeLabel(clinicDetails.station)}</strong>
              </div>
              {isConnectorStation(clinicDetails.station) && (
                <div className="detail-row">
                  <span>{t('healthHouseStations.connectorStatus')}</span>
                  <strong>{renderConnectorStatus(clinicDetails.station)}</strong>
                </div>
              )}
            </div>

            <div className="dialog-shell__actions">
              <button type="button" className="theme-button theme-button--ghost" onClick={closeClinicDetails}>
                {t('common.close')}
              </button>
            </div>
            </div>
          </div>
        </DialogPortal>
      )}

      <ConfirmDialog
        isOpen={deleteConfirm.show}
        onClose={() => setDeleteConfirm({ show: false, stationId: null, stationName: '' })}
        onConfirm={confirmDelete}
        title={t('healthHouseStations.confirmDelete')}
        message={t('healthHouseStations.confirmDeleteMessage', { name: deleteConfirm.stationName })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        type="danger"
      />
    </div>
  );
};

export default HealthHouseStations;
